import { LETTERHEAD_BAND } from './letterheadBand'
import { LETTERHEAD_FOOT } from './letterheadFoot'

export { letterheadArcCircles, LETTERHEAD_ARCS } from './letterheadArcs'

/**
 * The Portal Productions letterhead, as supplied by Tom (11 Sep 2026) as
 * `Portal Productions Blank Letterhead.docx`.
 *
 * The Word file carries the letterhead as one full-page A4 background image.
 * Rather than embed that whole page - most of which is blank paper, and all of
 * which would sit as a raster layer under every line of type - it is broken
 * into the three things that actually carry the brand:
 *
 *   band  the charcoal header bar with the official lockup and `Dubai, UAE`.
 *         Artwork, because the lockup cannot be redrawn or retyped, and a
 *         smooth gradient is exactly what JPEG is good at.
 *   arcs  the pale concentric motif, bottom right. NOT artwork - see
 *         assets/letterheadArcs.ts. It is eight perfect circles in an
 *         arithmetic progression, so it is drawn rather than stored.
 *   foot  the centred `Portal Productions - Dubai, UAE` line, set in the brand
 *         typeface (Darkmode, which this app does not carry and jsPDF could
 *         not embed). Artwork for that reason, and for that reason only.
 *
 * Positions were measured off the supplied artwork in pixels and converted at
 * 841.89pt / 2048px, so a call sheet printed from Portal OS and a Word
 * document written on the same letterhead line up sheet to sheet.
 *
 * SAFE_TOP and SAFE_BOTTOM follow from that geometry: the bar ends at 60.02pt
 * and the footer line starts at 813.11pt, so no content may be set above or
 * below those. The arc motif is deliberately not treated as an obstacle - it
 * is a pale watermark and type is meant to sit over it, exactly as it would
 * in a Word document.
 */
export const LETTERHEAD_A4 = {
  /** A4 portrait in points, which is what the letterhead was drawn for. */
  pageWidth: 595.28,
  pageHeight: 841.89,

  /** Lowest point the header bar reaches. Nothing may be set above this. */
  safeTop: 60.02,
  /** Highest point the footer line reaches. Nothing may be set below this. */
  safeBottom: 813.11,

  /**
   * The lockup's own left edge on the letterhead, at 121px. Body copy is set
   * to this same measure so the page hangs off one line rather than two.
   */
  lockupLeft: 49.73,

  /** The two pieces that genuinely have to be artwork. */
  images: [
    {
      alias: 'pp-letterhead-band',
      format: 'JPEG' as const,
      data: LETTERHEAD_BAND,
      x: 0,
      y: 0,
      width: 595.28,
      height: 60.02,
    },
    {
      alias: 'pp-letterhead-foot',
      format: 'PNG' as const,
      data: LETTERHEAD_FOOT,
      x: 230.2,
      y: 813.11,
      width: 144.7,
      height: 11.51,
    },
  ],
} as const

export type LetterheadImage = (typeof LETTERHEAD_A4)['images'][number]
